import React, { useEffect, useState, useRef } from "react";
import { decode } from "html-entities";
import { useModal } from "../../context/ModalContext";
import { fetchApi, getUploadsUrl } from "../../config";

const FALLBACK_FILTERS = [
    { id: "all", label: "ALL" },
    { id: "html", label: "HTML" },
    { id: "wordpress", label: "WORDPRESS" },
    { id: "php", label: "CUSTOM PHP" },
];

const FALLBACK_PROJECTS = [
    { cat: "wordpress", href: "https://www.chasclarkson.com.au/", blob: "blob-w2", initials: "CC", kicker: "RETAIL & EVENTS", title: "Chas Clarkson", text: "Christmas experiences for a heritage retail and events brand." },
    { cat: "php", href: "https://www.netrefine.com/", blob: "blob-w5", initials: "NR", kicker: "MANAGED IT", title: "Netrefine", text: "Every IT service and support, brought under one roof." },
    { cat: "landing", href: "https://roobster.io/", blob: "blob-w3", initials: "RB", kicker: "E-MOBILITY", title: "Roobster", text: "Brand and store for the future of urban e-mobility." },
    { cat: "wordpress", href: "https://silverspoons.com/", blob: "blob-w1", initials: "SS", kicker: "E-COMMERCE", title: "Silver Spoons", text: "A sustainable disposable-products store built on WooCommerce." },
];

/**
 * Filters + project grid. Replaces the data-filter/data-category
 * behaviour that kds-assets/js/main.js used to wire up by hand.
 */
export default function WorkGallery() {
    const [active, setActive] = useState("all");
    const [loading, setLoading] = useState(true);
    const { openModal } = useModal();
    const [filters, setFilters] = useState(FALLBACK_FILTERS);
    const [projects, setProjects] = useState(FALLBACK_PROJECTS);
    const projectsRef = useRef(projects);

    useEffect(() => { projectsRef.current = projects; }, [projects]);

    const visible = active === "all" ? projects : projects.filter((p) => p.cat === active);

    useEffect(() => {
        let mounted = true;
        async function load() {
            try {
                const res = await fetchApi("get_portfolio.php");
                if (!mounted) return;
                if (res && Array.isArray(res.Portfolio)) {
                    const mapped = res.Portfolio.map((p) => ({
                        id: p.id,
                        cat: (p.category_name || "").toLowerCase(),
                        href: p.site_url || "#",
                        initials: (p.site_title || "").split(" ").map(s => s[0]).slice(0,2).join('').toUpperCase(),
                        kicker: p.category_name || "",
                        title: p.site_title || p.description || "",
                        text: p.description || "",
                        site_image: p.site_image,
                        site_logo: p.site_logo,
                    }));

                    // derive unique categories
                    const cats = Array.from(new Set(mapped.map(m => m.cat).filter(Boolean)));
                    const newFilters = [{ id: 'all', label: 'ALL' }, ...cats.map(c => ({ id: c, label: c.toUpperCase() }))];
                    setFilters(newFilters);
                    setProjects(mapped);
                    // reset active if current active category no longer exists
                    if (active !== 'all' && !cats.includes(active)) setActive('all');
                }
            } catch (err) {
                // keep fallbacks
            } finally {
                if (mounted) setLoading(false);
            }
        }
        load();
        return () => { mounted = false; };
    }, []);

    return (
        <>
            <section className="container">
                <div className="filters">
                    {filters.map((f) => (
                        <button
                            className={`filter${active === f.id ? " is-active" : ""}`}
                            key={f.id}
                            onClick={() => setActive(f.id)}
                            type="button"
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </section>

            <section className="container sec-work-grid">
                <div className="work-grid" aria-busy={loading}>
                    {loading ? new Array(6).fill(0).map((_, index) => (
                        <div className="work-card card skeleton" key={`project-placeholder-${index}`}>
                            <div className="thumb">
                                <div className="graphic graphic--fill">
                                    <div className="graphic-center">
                                        <span className="placeholder-box" />
                                    </div>
                                </div>
                            </div>
                            <div className="body">
                                <span className="placeholder-line small" />
                                <span className="placeholder-line title" />
                                <span className="placeholder-line" />
                                <span className="placeholder-line" />
                            </div>
                        </div>
                    )) : visible.map((p) => (
                        <a className="work-card card" href={p.href} key={p.id || p.title} rel="noopener noreferrer" target="_blank">
                            <div className="thumb">
                                <div className="graphic graphic--fill">
                                    <span className="dots"></span>
                                    <div className="graphic-center">
                                            {p.site_image ? (
                                                <img className="site_image" src={getUploadsUrl('portfolio', p.site_image)} alt={p.site_image_name || p.title} />
                                            ) : p.site_logo ? (
                                                <img className="site_logo" src={getUploadsUrl('portfolio', p.site_logo)} alt={p.site_logo_name || p.title} />
                                            ) : (
                                                <span className="initials">{p.initials}</span>
                                            )}
                                    </div>
                                </div>
                            </div>
                            <div className="body">
                                <span className="kicker">{p.kicker}</span>
                                <h3>{decode(p.title)}</h3>
                                <p>{decode(p.text)}</p>
                            </div>
                        </a>
                    ))}
                </div>

                <div className="cta-band cta-work-mt">
                    <h2>Your project could be next.</h2>
                    <p>Tell us what you're building — we'll show you how we'd approach it.</p>
                    <button className="btn btn-primary" onClick={openModal} type="button">Start a project</button>
                </div>
            </section>
        </>
    );
}
